import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, Mail, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const faqs = [
  {
    question: "What are the festival dates?",
    answer: "The music programming runs from Friday 10am through to Sunday 11pm. Gates open Thursday afternoon for early access pass holders only."
  },
  {
    question: "Where is the festival located?",
    answer: "The festival is held at Maitland Showground in NSW, Australia."
  },
  {
    question: "Is this a camping festival?",
    answer: "Yes! Frequency Forest is a BYO camping festival. You'll need to bring your own tent, sleeping gear, and supplies. We also offer limited glamping options."
  },
  {
    question: "What is the age limit?",
    answer: "Frequency Forest is a strictly 18+ event. Valid photo ID is required at entry."
  },
  {
    question: "Can I bring my own food and drinks?",
    answer: "You may bring your own food and non-alcoholic beverages. This is NOT a BYO festival for alcohol – no alcohol may be brought in. We have a variety of food vendors and a fully licensed bar on site. No glass is permitted, and the festival is cashless."
  },
  {
    question: "What's your refund policy?",
    answer: "Tickets are generally non-refundable but may be transferred to another person. Please see our full Terms & Conditions for details."
  },
  {
    question: "Is there parking available?",
    answer: "Yes, there is on-site parking included with your festival ticket. Please follow signage and crew directions when you arrive."
  },
  {
    question: "Can I perform at Frequency Forest?",
    answer: "Yes! We'd love to hear from you. Email us at info@frequencyforest.com.au or head to the Applications section in the main menu to apply."
  },
  {
    question: "What if I lose something at the festival?",
    answer: "We have a lost and found at the info tent. After the event, please contact us via the form below with a description of your lost item."
  },
];

export default function FAQContact() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="faq-contact-page">
      <SEO 
        title="FAQ & Contact"
        description="Got questions about Frequency Forest 2026? Find answers about tickets, camping, and more. Contact us at info@frequencyforest.com.au for any enquiries."
      />
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="badge mb-4">We're Here to Help</span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown mb-6">
              FAQ & Contact
            </h1>
            <p className="text-ff-brown/70 text-lg max-w-2xl mx-auto">
              We're a small team doing big things. Please check the FAQ below before reaching out – 
              your answer might already be here!
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="font-display text-2xl text-ff-brown mb-8 text-center">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white/30 border-2 border-ff-outline/20 rounded-xl overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-4 text-ff-brown hover:text-ff-brown text-left font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-ff-brown/70">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-ff-green">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display text-3xl text-ff-brown mb-6">
                Get in Touch
              </h2>
              <p className="text-ff-brown/70 mb-8">
                Can't find your answer above? We're happy to help! Please be patient with our 
                responses – we're a small team with big hearts.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-ff-brown flex-shrink-0" />
                  <div>
                    <h3 className="text-ff-brown font-medium mb-1">Email Us</h3>
                    <a href="mailto:info@frequencyforest.com.au" className="text-ff-brown/60 text-sm hover:text-ff-brown hover:underline" data-testid="contact-email-link">
                      info@frequencyforest.com.au
                    </a>
                  </div>
                </div>
                
                <a href="https://www.instagram.com/frequencyforest.au/" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group" data-testid="contact-instagram-link">
                  <Instagram className="w-6 h-6 text-ff-brown flex-shrink-0" />
                  <div>
                    <h3 className="text-ff-brown font-medium mb-1 group-hover:underline">Follow Us</h3>
                    <p className="text-ff-brown/60 text-sm">
                      Stay updated and DM us on Instagram for quick questions: @frequencyforest.au
                    </p>
                  </div>
                </a>
                
                <a href="https://www.facebook.com/people/Frequency-Forest/61590454420774/" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group" data-testid="contact-facebook-link">
                  <Facebook className="w-6 h-6 text-ff-brown flex-shrink-0" />
                  <div>
                    <h3 className="text-ff-brown font-medium mb-1 group-hover:underline">Join the Community</h3>
                    <p className="text-ff-brown/60 text-sm">
                      Follow our Facebook page to connect with fellow forest dwellers.
                    </p>
                  </div>
                </a>
              </div>

              <div className="mt-8 bg-white/40 p-6 rounded-2xl">
                <h3 className="font-display text-lg text-ff-brown mb-3">
                  Response Times
                </h3>
                <p className="text-ff-brown/60 text-sm">
                  We typically respond within 2-5 business days. During peak times (close to the event), 
                  responses may take a bit longer. Thank you for your patience!
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white/40 p-8 rounded-2xl">
                <h3 className="font-display text-2xl text-ff-brown mb-6">
                  Send a Message
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="contact-first-name"
                      />
                    </div>
                    <div>
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="contact-last-name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-phone"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input min-h-[150px] resize-none"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    Send Message
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
